import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

type FilterList = Record<string, Set<string>>;

interface FilterEvent {
  source: 'table' | 'chart' | 'kpi';
  field: string;
  value: string;
  action: 'add' | 'remove' | 'clear';
}

interface FilterState {
  appliedFilters: FilterList;
  availableOptions: FilterList;
  filteredData: any[];
  totalFilterCount: number;
}

@Injectable({
  providedIn: 'root'
})
class KpiChartFilterService {
  private sourceData: any[] = [];
  private _filterState = new BehaviorSubject<FilterState>({
    appliedFilters: {},
    availableOptions: {},
    filteredData: [],
    totalFilterCount: 0
  });

  private _filterEvents = new BehaviorSubject<FilterEvent | null>(null);

  filterState$: Observable<FilterState> = this._filterState.asObservable();
  filterEvents$: Observable<FilterEvent | null> = this._filterEvents.asObservable();

  /**
   * Initialize the filter service with source data
   */
  initializeFilters(data: any[]): void {
    this.sourceData = [...data];
    const availableOptions = this.createFilterOptions(data);

    this._filterState.next({
      appliedFilters: {},
      availableOptions,
      filteredData: [...data],
      totalFilterCount: 0
    });
  }

  /**
   * Add a filter from any source (table, chart, KPI)
   */
  addFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };

    if (!newFilters[field]) {
      newFilters[field] = new Set<string>();
    }
    newFilters[field].add(value);

    this.updateFilterState(newFilters, source, field, value, 'add');
  }

  /**
   * Remove a specific filter
   */
  removeFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };

    if (newFilters[field]) {
      newFilters[field].delete(value);

      if (newFilters[field].size === 0) {
        delete newFilters[field];
      }
    }

    this.updateFilterState(newFilters, source, field, value, 'remove');
  }

  /**
   * Clear all filters for a specific field
   */
  clearFieldFilters(field: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const newFilters = { ...currentState.appliedFilters };
    delete newFilters[field];

    this.updateFilterState(newFilters, source, field, '', 'clear');
  }

  /**
   * Clear all filters
   */
  clearAllFilters(source: 'table' | 'chart' | 'kpi' = 'table'): void {
    this.updateFilterState({}, source, '', '', 'clear');
  }

  /**
   * Toggle a filter (add if not present, remove if present)
   */
  toggleFilter(field: string, value: string, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    const currentState = this._filterState.value;
    const fieldFilters = currentState.appliedFilters[field];

    if (fieldFilters && fieldFilters.has(value)) {
      this.removeFilter(field, value, source);
    } else {
      this.addFilter(field, value, source);
    }
  }

  /**
   * Set multiple filters at once (useful for chart interactions)
   */
  setFilters(filters: FilterList, source: 'table' | 'chart' | 'kpi' = 'table'): void {
    this.updateFilterState(filters, source, '', '', 'add');
  }

  /**
   * Get current filter state synchronously
   */
  getCurrentState(): FilterState {
    return this._filterState.value;
  }

  /**
   * Check if a specific filter is applied
   */
  isFilterApplied(field: string, value: string): boolean {
    const currentState = this._filterState.value;
    const fieldFilters = currentState.appliedFilters[field];
    if (!fieldFilters) {
      return false;
    }

    return fieldFilters.has(value);
  }

  /**
   * Apply filters and update state
   */
  private updateFilterState(
    newFilters: FilterList,
    source: 'table' | 'chart' | 'kpi',
    field: string,
    value: string,
    action: 'add' | 'remove' | 'clear'
  ): void {
    const filteredData = this.applyFilters(this.sourceData, newFilters);
    const totalFilterCount = Object.values(newFilters).reduce(
      (sum, set) => sum + (set instanceof Set ? set.size : 0),
      0
    );

    const newState: FilterState = {
      appliedFilters: newFilters,
      availableOptions: this._filterState.value.availableOptions,
      filteredData,
      totalFilterCount
    };

    this._filterState.next(newState);

    // Emit filter event for components to react to
    if (field || action === 'clear') {
      this._filterEvents.next({ source, field, value, action });
    }
  }

  private createFilterOptions<T extends object>(sourceData: T[]): Record<keyof T, Set<string>> {
    const filterOptions = {} as Record<keyof T, Set<string>>;

    for (const data of sourceData) {
      (Object.keys(data) as (keyof T)[]).forEach((column) => {
        if (!filterOptions[column]) {
          filterOptions[column] = new Set<string>();
        }

        const value = data[column];
        if (value !== null && value !== undefined && value !== '') {
          filterOptions[column].add(String(value));
        }
      });
    }

    (Object.keys(filterOptions) as (keyof T)[]).forEach((key) => {
      filterOptions[key].delete('');
    });

    return filterOptions;
  }

  private applyFilters<T extends object>(
    sourceData: T[],
    filtersApplied: Record<keyof T, Set<string>>
  ): T[] {
    let filteredData = [...sourceData];

    for (const [field, criteria] of Object.entries(filtersApplied) as [keyof T, Set<string>][]) {
      if (criteria.size === 0) continue;

      filteredData = filteredData.filter((item) => {
        const value = item[field];
        return criteria.has(value !== undefined && value !== null ? String(value) : '');
      });
    }

    return filteredData;
  }
}

export type { FilterEvent, FilterList, FilterState };
export { KpiChartFilterService };
